import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

import Select from '../../../components/ui/Select';

const StatusBadge = ({ status }) => {
  const map = {
    pending: 'bg-warning/10 text-warning border border-warning/20',
    under_review: 'bg-primary/10 text-primary border border-primary/20',
    approved: 'bg-success/10 text-success border border-success/20',
    rejected: 'bg-error/10 text-error border border-error/20',
  };
  const labels = {
    pending: 'Pending',
    under_review: 'Under Review',
    approved: 'Approved',
    rejected: 'Rejected',
  };
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${map?.[status] || 'bg-muted text-muted-foreground'}`}>
      {labels?.[status] || status}
    </span>
  );
};

const DocumentViewerModal = ({ url, label, onClose }) => (
  <div className="fixed inset-0 bg-black/80 z-[300] flex items-center justify-center p-4" onClick={onClose}>
    <div className="bg-card rounded-2xl border border-border w-full max-w-2xl max-h-[90vh] overflow-hidden" onClick={e => e?.stopPropagation()}>
      <div className="flex items-center justify-between p-4 border-b border-border">
        <h4 className="text-sm font-semibold text-foreground">{label}</h4>
        <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted text-muted-foreground">
          <Icon name="X" size={16} />
        </button>
      </div>
      <div className="p-4 flex items-center justify-center min-h-[300px]">
        {url ? (
          url?.endsWith('.pdf') ? (
            <iframe src={url} className="w-full h-[500px] rounded-lg" title={label} />
          ) : (
            <img src={url} alt={label} className="max-w-full max-h-[500px] rounded-lg object-contain" />
          )
        ) : (
          <div className="flex flex-col items-center gap-3 text-muted-foreground">
            <Icon name="FileX" size={40} />
            <p className="text-sm">Document not available</p>
          </div>
        )}
      </div>
    </div>
  </div>
);

const ApplicationDetailModal = ({ application, onClose, onStatusUpdate }) => {
  const [action, setAction] = useState(null); // 'approve' | 'reject'
  const [complianceNote, setComplianceNote] = useState(application?.compliance_notes || '');
  const [rejectReason, setRejectReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [docViewer, setDocViewer] = useState(null); // { url, label }

  const handleApprove = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        ?.from('card_applications')
        ?.update({ status: 'approved', compliance_notes: complianceNote, updated_at: new Date()?.toISOString() })
        ?.eq('id', application?.id);
      if (!error) {
        onStatusUpdate(application?.id, 'approved', complianceNote);
        onClose();
      }
    } catch (err) {
      console.error('[CardApplicationsReview] approve error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason?.trim()) return;
    setSaving(true);
    try {
      const note = complianceNote ? `${complianceNote}\n\nRejection Reason: ${rejectReason}` : `Rejection Reason: ${rejectReason}`;
      const { error } = await supabase
        ?.from('card_applications')
        ?.update({ status: 'rejected', compliance_notes: note, updated_at: new Date()?.toISOString() })
        ?.eq('id', application?.id);
      if (!error) {
        onStatusUpdate(application?.id, 'rejected', note);
        onClose();
      }
    } catch (err) {
      console.error('[CardApplicationsReview] reject error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleMarkUnderReview = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        ?.from('card_applications')
        ?.update({ status: 'under_review', updated_at: new Date()?.toISOString() })
        ?.eq('id', application?.id);
      if (!error) {
        onStatusUpdate(application?.id, 'under_review', application?.compliance_notes);
        onClose();
      }
    } catch (err) {
      console.error('[CardApplicationsReview] under_review error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNote = async () => {
    setSaving(true);
    try {
      await supabase
        ?.from('card_applications')
        ?.update({ compliance_notes: complianceNote, updated_at: new Date()?.toISOString() })
        ?.eq('id', application?.id);
    } catch (err) {
      console.error('[CardApplicationsReview] save note error:', err);
    } finally {
      setSaving(false);
    }
  };

  const addr = application?.address_json || {};
  const addressStr = [addr?.street, addr?.city, addr?.state, addr?.country]?.filter(Boolean)?.join(', ');

  const docs = [
    { label: 'Document Front', url: application?.document_front_url },
    { label: 'Document Back', url: application?.document_back_url },
    { label: 'Selfie / Liveness', url: application?.selfie_url },
  ];

  return (
    <>
      {docViewer && (
        <DocumentViewerModal url={docViewer?.url} label={docViewer?.label} onClose={() => setDocViewer(null)} />
      )}
      <div className="fixed inset-0 bg-black/70 z-[200] flex items-center justify-center p-4">
        <div className="bg-card rounded-2xl border border-border w-full max-w-3xl max-h-[92vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-border sticky top-0 bg-card z-10">
            <div className="flex items-center gap-3">
              <h3 className="text-base font-semibold text-foreground">Application Review</h3>
              <StatusBadge status={application?.status} />
            </div>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted text-muted-foreground">
              <Icon name="X" size={16} />
            </button>
          </div>

          <div className="p-5 space-y-6">
            {/* Applicant Info */}
            <section>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Personal Information</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'Full Name', value: application?.full_name },
                  { label: 'Date of Birth', value: application?.dob },
                  { label: 'Nationality', value: application?.nationality },
                  { label: 'Phone', value: application?.phone },
                  { label: 'Employment', value: application?.employment_status },
                  { label: 'Income Range', value: application?.income_range },
                  { label: 'Address', value: addressStr || '—' },
                  { label: 'Submitted', value: application?.created_at ? new Date(application?.created_at)?.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—' },
                ]?.map((d, i) => (
                  <div key={i} className="bg-muted/40 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground mb-1">{d?.label}</p>
                    <p className="text-sm font-semibold text-foreground">{d?.value || '—'}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Social / SC Score */}
            <section>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Social Profile & Eligibility</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'Social Handle', value: application?.social_handle },
                  { label: 'Platform', value: application?.platform },
                  { label: 'Audience Size', value: application?.audience_size },
                  { label: 'Monthly Earnings', value: application?.monthly_earnings },
                ]?.map((d, i) => (
                  <div key={i} className="bg-muted/40 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground mb-1">{d?.label}</p>
                    <p className="text-sm font-semibold text-foreground">{d?.value || '—'}</p>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex items-center gap-3 p-3 bg-primary/10 rounded-lg border border-primary/20">
                <Icon name="Star" size={18} color="var(--color-primary)" />
                <div>
                  <p className="text-xs text-muted-foreground">SocialCents Score</p>
                  <p className="text-lg font-bold text-primary">{application?.sc_score ?? 0}</p>
                </div>
                <div className="ml-auto">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                    (application?.sc_score ?? 0) >= 700 ? 'bg-success/10 text-success' :
                    (application?.sc_score ?? 0) >= 500 ? 'bg-warning/10 text-warning': 'bg-error/10 text-error'
                  }`}>
                    {(application?.sc_score ?? 0) >= 700 ? 'Eligible' : (application?.sc_score ?? 0) >= 500 ? 'Borderline' : 'Not Eligible'}
                  </span>
                </div>
              </div>
            </section>

            {/* KYC Documents */}
            <section>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">KYC Documents</p>
              <div className="flex items-center gap-2 mb-3 p-2 bg-muted/30 rounded-lg">
                <Icon name="FileText" size={14} color="var(--color-primary)" />
                <span className="text-xs text-muted-foreground">Document Type:</span>
                <span className="text-xs font-semibold text-foreground">{application?.document_type || '—'}</span>
                {application?.document_expiry && (
                  <span className="ml-auto text-xs text-muted-foreground">Expires: {application?.document_expiry}</span>
                )}
              </div>
              <div className="grid grid-cols-3 gap-3">
                {docs?.map((doc, i) => (
                  <button
                    key={i}
                    onClick={() => setDocViewer({ url: doc?.url, label: doc?.label })}
                    className="group flex flex-col items-center gap-2 p-4 bg-muted/40 rounded-xl border border-border hover:border-primary/40 hover:bg-primary/5 transition-all"
                  >
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                      <Icon name={i === 2 ? 'Camera' : 'FileImage'} size={18} color="var(--color-primary)" />
                    </div>
                    <span className="text-xs font-medium text-foreground text-center">{doc?.label}</span>
                    <span className="text-[10px] text-primary flex items-center gap-1">
                      <Icon name="Eye" size={10} /> View
                    </span>
                  </button>
                ))}
              </div>
            </section>

            {/* Compliance Notes */}
            <section>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Compliance Notes</p>
              <textarea
                value={complianceNote}
                onChange={e => setComplianceNote(e?.target?.value)}
                placeholder="Add internal compliance notes, observations, or flags for this application..."
                rows={4}
                className="w-full bg-muted/40 border border-border rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 transition-all"
              />
              <button
                onClick={handleSaveNote}
                disabled={saving}
                className="mt-2 text-xs text-primary hover:text-primary/80 font-medium flex items-center gap-1 transition-colors"
              >
                <Icon name="Save" size={12} />
                {saving ? 'Saving...' : 'Save Note'}
              </button>
            </section>

            {/* Actions */}
            <section className="border-t border-border pt-5">
              {action === 'reject' ? (
                <div className="space-y-3">
                  <p className="text-sm font-semibold text-foreground">Rejection Reason</p>
                  <textarea
                    value={rejectReason}
                    onChange={e => setRejectReason(e?.target?.value)}
                    placeholder="Provide a clear reason for rejection (required)..."
                    rows={3}
                    className="w-full bg-muted/40 border border-border rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:border-error/50 focus:ring-1 focus:ring-error/20 transition-all"
                  />
                  <div className="flex gap-3">
                    <Button variant="outline" onClick={() => setAction(null)} className="flex-1">Cancel</Button>
                    <Button
                      onClick={handleReject}
                      disabled={!rejectReason?.trim() || saving}
                      className="flex-1 bg-error hover:bg-error/90 text-white border-error"
                    >
                      {saving ? 'Rejecting...' : 'Confirm Rejection'}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap gap-3">
                  {application?.status !== 'under_review' && (
                    <Button
                      variant="outline"
                      onClick={handleMarkUnderReview}
                      disabled={saving}
                      className="flex items-center gap-2"
                    >
                      <Icon name="Search" size={14} />
                      Mark Under Review
                    </Button>
                  )}
                  {application?.status !== 'approved' && (
                    <Button
                      onClick={handleApprove}
                      disabled={saving}
                      className="flex items-center gap-2 bg-success hover:bg-success/90 text-white border-success"
                    >
                      <Icon name="CheckCircle" size={14} />
                      {saving ? 'Approving...' : 'Approve Application'}
                    </Button>
                  )}
                  {application?.status !== 'rejected' && (
                    <Button
                      variant="outline"
                      onClick={() => setAction('reject')}
                      disabled={saving}
                      className="flex items-center gap-2 text-error border-error/40 hover:bg-error/10"
                    >
                      <Icon name="XCircle" size={14} />
                      Reject
                    </Button>
                  )}
                </div>
              )}
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

const CardApplicationsReview = ({ currentAdminId }) => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedApp, setSelectedApp] = useState(null);
  const [stats, setStats] = useState({ total: 0, pending: 0, under_review: 0, approved: 0, rejected: 0 });

  const loadApplications = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('card_applications')
        ?.select(`
          id, full_name, dob, nationality, address_json, phone,
          employment_status, income_range, document_type,
          document_front_url, document_back_url, selfie_url,
          document_expiry, social_handle, platform, audience_size,
          monthly_earnings, sc_score, status, compliance_notes,
          created_at, updated_at, user_id
        `)
        ?.order('created_at', { ascending: false });

      if (!error && data) {
        setApplications(data);
        const s = { total: data?.length, pending: 0, under_review: 0, approved: 0, rejected: 0 };
        data?.forEach(a => { if (s?.[a?.status] !== undefined) s[a?.status]++; });
        setStats(s);
      }
    } catch (err) {
      console.error('[CardApplicationsReview] load error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  const handleStatusUpdate = (id, newStatus, newNote) => {
    setApplications(prev =>
      prev?.map(a => a?.id === id ? { ...a, status: newStatus, compliance_notes: newNote } : a)
    );
    const updated = applications?.map(a => a?.id === id ? { ...a, status: newStatus } : a);
    const s = { total: updated?.length, pending: 0, under_review: 0, approved: 0, rejected: 0 };
    updated?.forEach(a => { if (s?.[a?.status] !== undefined) s[a?.status]++; });
    setStats(s);
  };

  const filtered = applications?.filter(a => {
    const matchSearch = !search ||
      a?.full_name?.toLowerCase()?.includes(search?.toLowerCase()) ||
      a?.social_handle?.toLowerCase()?.includes(search?.toLowerCase()) ||
      a?.platform?.toLowerCase()?.includes(search?.toLowerCase());
    const matchStatus = statusFilter === 'all' || a?.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const statusOptions = [
    { value: 'all', label: 'All Applications' },
    { value: 'pending', label: 'Pending' },
    { value: 'under_review', label: 'Under Review' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
  ];

  const statCards = [
    { label: 'Total', value: stats?.total, icon: 'CreditCard', color: 'text-foreground', bg: 'bg-muted/40' },
    { label: 'Pending', value: stats?.pending, icon: 'Clock', color: 'text-warning', bg: 'bg-warning/10' },
    { label: 'Under Review', value: stats?.under_review, icon: 'Search', color: 'text-primary', bg: 'bg-primary/10' },
    { label: 'Approved', value: stats?.approved, icon: 'CheckCircle', color: 'text-success', bg: 'bg-success/10' },
    { label: 'Rejected', value: stats?.rejected, icon: 'XCircle', color: 'text-error', bg: 'bg-error/10' },
  ];

  return (
    <div className="space-y-6">
      {selectedApp && (
        <ApplicationDetailModal
          application={selectedApp}
          onClose={() => setSelectedApp(null)}
          onStatusUpdate={handleStatusUpdate}
        />
      )}
      {/* Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {statCards?.map((s, i) => (
          <div key={i} className={`${s?.bg} rounded-xl p-4 flex flex-col gap-1`}>
            <div className="flex items-center gap-2">
              <Icon name={s?.icon} size={15} className={s?.color} />
              <span className="text-xs text-muted-foreground">{s?.label}</span>
            </div>
            <p className={`text-2xl font-bold ${s?.color}`}>{s?.value}</p>
          </div>
        ))}
      </div>
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Icon name="Search" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search by name, handle, platform..."
            value={search}
            onChange={e => setSearch(e?.target?.value)}
            className="w-full pl-9 pr-4 py-2.5 bg-muted/40 border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 transition-all"
          />
        </div>
        <Select
          options={statusOptions}
          value={statusFilter}
          onChange={val => setStatusFilter(val)}
          className="sm:w-48"
        />
        <button
          onClick={loadApplications}
          className="flex items-center gap-2 px-4 py-2.5 bg-muted/40 border border-border rounded-xl text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-all"
        >
          <Icon name="RefreshCw" size={14} />
          Refresh
        </button>
      </div>
      {/* Applications Table */}
      <div className="bg-card rounded-2xl border border-border overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h3 className="text-sm font-semibold text-foreground">Card Applications</h3>
          <span className="text-xs text-muted-foreground">{filtered?.length} result{filtered?.length !== 1 ? 's' : ''}</span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-muted-foreground">Loading applications...</p>
            </div>
          </div>
        ) : filtered?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <div className="w-12 h-12 rounded-full bg-muted/40 flex items-center justify-center">
              <Icon name="CreditCard" size={22} className="text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground">
              {search || statusFilter !== 'all' ? 'No applications match your filters' : 'No card applications yet'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/20">
                  {['Applicant', 'Platform / Handle', 'SC Score', 'Document', 'Submitted', 'Status', 'Actions']?.map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered?.map(app => (
                  <tr key={app?.id} className="hover:bg-muted/20 transition-colors">
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Icon name="User" size={14} color="var(--color-primary)" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-foreground">{app?.full_name || '—'}</p>
                          <p className="text-xs text-muted-foreground">{app?.nationality || '—'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <div>
                        <p className="text-sm font-medium text-foreground capitalize">{app?.platform || '—'}</p>
                        <p className="text-xs text-muted-foreground">{app?.social_handle || '—'}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className={`text-sm font-bold ${
                        (app?.sc_score ?? 0) >= 700 ? 'text-success' :
                        (app?.sc_score ?? 0) >= 500 ? 'text-warning' : 'text-error'
                      }`}>
                        {app?.sc_score ?? 0}
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="text-xs text-muted-foreground capitalize">{app?.document_type || '—'}</span>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="text-xs text-muted-foreground">
                        {app?.created_at ? new Date(app?.created_at)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      <StatusBadge status={app?.status} />
                    </td>
                    <td className="px-4 py-3.5">
                      <button
                        onClick={() => setSelectedApp(app)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 hover:bg-primary/20 text-primary rounded-lg text-xs font-medium transition-colors"
                      >
                        <Icon name="Eye" size={12} />
                        Review
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CardApplicationsReview;
